/**
 * Public Response Form App
 * Minimal app for respondents to fill out forms.
 *
 * Routing (see main.tsx):
 *   /respond/:requestId          — new submission (URL params captured)
 *   /respond/:requestId/r/:responseId — view / edit an existing response
 *                                        or submit a server-side prefill draft
 */

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Text,
  Spinner,
  Card,
  CardPreview,
  Link,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { CheckmarkCircleRegular, LockClosedRegular, EditRegular } from '@fluentui/react-icons';
import { DynamicForm } from '@structured-request/components';
import { createApiClient } from '@structured-request/api-client';
import { validateForm } from '@structured-request/core';
import type { PublicRequestTemplate, FormResponse } from '@structured-request/core';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  loading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '300px',
    gap: tokens.spacingVerticalM,
  },
  success: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingVerticalXXL,
    textAlign: 'center',
    gap: tokens.spacingVerticalM,
  },
  successIcon: {
    color: tokens.colorPaletteGreenForeground1,
    fontSize: '64px',
  },
  lockedIcon: {
    color: tokens.colorNeutralForeground3,
    fontSize: '48px',
  },
  errorCard: {
    padding: tokens.spacingVerticalXL,
    textAlign: 'center',
  },
  responseUrl: {
    marginTop: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalS,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    wordBreak: 'break-all',
  },
});

const apiClient = createApiClient();

// System-level query params that should NOT be captured as urlParams
const SYSTEM_PARAMS = new Set(['mspId', 'userId']);

const App: React.FC = () => {
  const styles = useStyles();
  const { requestId, responseId } = useParams<{ requestId: string; responseId?: string }>();
  const [searchParams] = useSearchParams();

  const [template, setTemplate] = useState<PublicRequestTemplate | null>(null);
  const [existingResponse, setExistingResponse] = useState<FormResponse | null>(null);
  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submittedResponseId, setSubmittedResponseId] = useState<string | null>(null);
  // Stable client-generated ID used as the blob prefix for file uploads on new submissions
  const pendingResponseId = useRef(`pending_${crypto.randomUUID()}`);

  /** Capture non-system URL params to track external context (CRM IDs, etc.) */
  const urlParams: Record<string, string> = {};
  searchParams.forEach((value, key) => {
    if (!SYSTEM_PARAMS.has(key)) urlParams[key] = value;
  });

  const mspId = searchParams.get('mspId') ?? 'test-msp';
  const uploadContextId = responseId ?? existingResponse?.id ?? pendingResponseId.current;
  const uploadContext = { mspId, responseId: uploadContextId };

  const isEditMode = Boolean(responseId);
  const isLocked = existingResponse?.locked === true;
  const canEdit =
    isEditMode &&
    !isLocked &&
    !existingResponse?.isDraft &&
    template?.responseEditMode === 'editable';

  useEffect(() => {
    const load = async () => {
      if (!requestId) {
        setLoadError('Invalid request URL. Please check the link and try again.');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);

        // Always fetch the template (needed for field rendering)
        let tmpl;
        try {
          tmpl = await apiClient.getPublicRequest(requestId);
        } catch (err: any) {
          const status = err.response?.status;
          const apiMsg = err.response?.data?.error;
          if (status === 404) {
            setLoadError('This form could not be found. Please check the link and try again.');
          } else {
            setLoadError(
              `Unable to load the form. Please try again later.${apiMsg ? ` (${apiMsg})` : ''}`
            );
          }
          // eslint-disable-next-line no-console
          console.error('[SR] getPublicRequest failed:', status, apiMsg, err);
          return;
        }

        // If the template requires auth and the user isn't on the /secure/ path,
        // redirect there now. SWA will enforce Entra login before the app reloads.
        if (tmpl.requiresAuth && !window.location.pathname.startsWith('/secure/')) {
          window.location.replace(`/secure${window.location.pathname}${window.location.search}`);
          return;
        }

        setTemplate(tmpl);

        // Base initial data: template defaults → localStorage → URL param prefill
        const initialData: Record<string, unknown> = {};

        for (const field of tmpl.fields) {
          if (field.defaultValue !== undefined) {
            initialData[field.name] = field.defaultValue;
          }
        }

        // Re-hydrate browser-persisted values (non-secure, storage-enabled fields)
        for (const field of tmpl.fields) {
          if (field.enableStorage && !field.isSecure) {
            const stored = localStorage.getItem(`sr_field_${requestId}_${field.name}`);
            if (stored !== null) {
              try {
                initialData[field.name] = JSON.parse(stored);
              } catch {
                initialData[field.name] = stored;
              }
            }
          }
        }

        // Feature 2A: URL params that match field names act as prefill values
        for (const field of tmpl.fields) {
          if (urlParams[field.name] !== undefined) {
            initialData[field.name] = urlParams[field.name];
          }
        }

        if (responseId) {
          // Feature 2B / Feature 4: load an existing response (draft or submitted)
          try {
            const resp = await apiClient.getPublicResponse(requestId, responseId);
            setExistingResponse(resp);
            // Overlay saved response data on top of defaults
            Object.assign(initialData, resp.data);
          } catch (err: any) {
            const status = err.response?.status;
            // eslint-disable-next-line no-console
            console.error('[SR] getPublicResponse failed:', status, err.response?.data, err);
            if (status === 404) {
              // Response not found — fall through and show the form for a fresh submission
              // (the response URL may have expired or been deleted)
            } else {
              setLoadError(
                `Unable to load your saved response. Please try again later.`
              );
              return;
            }
          }
        }

        setFormData(initialData);
      } catch (err: any) {
        // eslint-disable-next-line no-console
        console.error('[SR] Unexpected load error:', err);
        setLoadError('Unable to load the form. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId, responseId]);

  const handleFieldChange = (name: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (template && requestId) {
      const field = template.fields.find((f) => f.name === name);
      if (field?.enableStorage && !field.isSecure) {
        try {
          localStorage.setItem(`sr_field_${requestId}_${field.name}`, JSON.stringify(value));
        } catch {
          // localStorage may be unavailable in private browsing
        }
      }
    }

    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleSubmit = async (data: Record<string, unknown>) => {
    if (!template || !requestId) return;

    const validation = validateForm(template.fields, data);
    if (!validation.valid) {
      setErrors(validation.errors);
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitError(null);

      // Feature 1: pass captured URL params with every submission
      const dto = {
        data,
        urlParams: Object.keys(urlParams).length ? urlParams : undefined,
      };

      let result;

      if (responseId) {
        // Edit mode: update existing (draft or submitted-editable)
        result = await apiClient.updatePublicResponse(requestId, responseId, dto);
        setSubmittedResponseId(responseId);
      } else {
        // New submission
        result = await apiClient.submitResponse(requestId, dto);
        setSubmittedResponseId(result.data?.id ?? null);
      }

      if (!result.success) {
        setSubmitError(result.error ?? 'Submission failed. Please try again.');
        return;
      }

      setIsSuccess(true);
    } catch (err: any) {
      if (err.response?.status === 410) {
        setSubmitError('This request is no longer accepting responses.');
      } else if (err.response?.status === 403) {
        setSubmitError('This response is locked and cannot be edited.');
      } else {
        setSubmitError('Failed to submit your response. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <Spinner size="large" />
        <Text>Loading form...</Text>
      </div>
    );
  }

  if (loadError) {
    return (
      <Card>
        <CardPreview className={styles.errorCard}>
          <Text size={500} weight="semibold" block>
            Unable to Load Form
          </Text>
          <Text style={{ marginTop: tokens.spacingVerticalM }}>{loadError}</Text>
        </CardPreview>
      </Card>
    );
  }

  // Feature 3: show locked notice for readonly submitted responses in view mode
  if (isEditMode && existingResponse && !existingResponse.isDraft && !canEdit && !isSuccess) {
    return (
      <Card>
        <CardPreview className={styles.success}>
          {isLocked ? (
            <LockClosedRegular className={styles.lockedIcon} />
          ) : (
            <EditRegular className={styles.lockedIcon} />
          )}
          <Text size={600} weight="semibold">
            {isLocked ? 'Response Locked' : 'Response Submitted'}
          </Text>
          <Text>
            {isLocked
              ? 'This response has been locked by an administrator and cannot be changed.'
              : 'This response has been submitted and cannot be edited.'}
          </Text>
          {/* Still render a read-only view of the data */}
          <DynamicForm
            template={template!}
            data={formData}
            onChange={() => {}}
            onSubmit={() => {}}
            errors={{}}
            isSubmitting={false}
            readOnly
            uploadContext={uploadContext}
          />
        </CardPreview>
      </Card>
    );
  }

  if (isSuccess) {
    // Feature 4: direct response URL on completion page
    const responseUrl =
      template?.showResponseUrl && submittedResponseId
        ? apiClient.generateResponseUrl(requestId!, submittedResponseId)
        : null;

    return (
      <Card>
        <CardPreview className={styles.success}>
          <CheckmarkCircleRegular className={styles.successIcon} />
          <Text size={600} weight="semibold">
            {isEditMode ? 'Response Updated' : 'Thank You!'}
          </Text>
          <Text>
            {isEditMode
              ? 'Your changes have been saved successfully.'
              : 'Your response has been submitted successfully.'}
          </Text>
          {responseUrl && (
            <>
              <Text weight="semibold" style={{ marginTop: tokens.spacingVerticalM }}>
                Your response link:
              </Text>
              <div className={styles.responseUrl}>
                <Link href={responseUrl}>{responseUrl}</Link>
              </div>
              <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                Bookmark this link to review or update your response later.
              </Text>
            </>
          )}
        </CardPreview>
      </Card>
    );
  }

  if (!template) {
    return null;
  }

  return (
    <div className={styles.container}>
      <DynamicForm
        template={template}
        data={formData}
        onChange={handleFieldChange}
        onSubmit={handleSubmit}
        errors={errors}
        isSubmitting={isSubmitting}
        errorMessage={submitError || undefined}
        submitLabel={isEditMode ? 'Save Changes' : 'Submit Response'}
        uploadContext={uploadContext}
      />
    </div>
  );
};

export default App;

